import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import { TextInput, Button, Text } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { authService } from '../../services/authService';
import { colors, spacing, typography } from '../../constants/theme';
import { getErrorMessage } from '../../utils/errorMessages';

const ChangePasswordScreen: React.FC = () => {
  const navigation = useNavigation();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const showAlert = (title: string, message: string, onOk?: () => void) => {
    if (Platform.OS === 'web') {
      if (title === 'Success') {
        setSuccessMessage(message);
        setErrorMessage('');
        if (onOk) {
          setTimeout(onOk, 2000);
        }
      } else {
        setErrorMessage(message);
        setSuccessMessage('');
      }
    } else {
      Alert.alert(title, message, onOk ? [{ text: 'OK', onPress: onOk }] : undefined);
    }
  };

  const validateForm = () => {
    if (!currentPassword.trim()) {
      showAlert('Error', 'Please enter your current password');
      return false;
    }

    if (!newPassword.trim()) {
      showAlert('Error', 'Please enter a new password');
      return false;
    }

    if (newPassword.length < 8) {
      showAlert('Error', 'New password must be at least 8 characters long');
      return false;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(newPassword)) {
      showAlert('Error', 'New password must contain at least one letter and one number');
      return false;
    }

    if (newPassword !== confirmPassword) {
      showAlert('Error', 'New password and confirm password do not match');
      return false;
    }

    return true;
  };

  const handleChangePassword = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      await authService.changePassword(currentPassword, newPassword);
      
      showAlert(
        'Success',
        'Password updated successfully',
        () => navigation.goBack()
      );
    } catch (error: unknown) {
      showAlert('Error', getErrorMessage(error, 'Failed to change password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formContainer}>
          <Text style={styles.title}>Change Password</Text>
          <Text style={styles.subtitle}>
            Enter your current password and choose a new secure password
          </Text>

          <TextInput
            label="Current Password"
            value={currentPassword}
            onChangeText={setCurrentPassword}
            secureTextEntry={!showCurrentPassword}
            mode="outlined"
            style={styles.input}
            disabled={isLoading}
            left={<TextInput.Icon icon="lock" />}
            right={
              <TextInput.Icon
                icon={showCurrentPassword ? 'eye-off' : 'eye'}
                onPress={() => setShowCurrentPassword(!showCurrentPassword)}
              />
            }
          />

          <TextInput
            label="New Password"
            value={newPassword}
            onChangeText={setNewPassword}
            secureTextEntry={!showNewPassword}
            mode="outlined"
            style={styles.input}
            disabled={isLoading}
            left={<TextInput.Icon icon="lock-plus" />}
            right={
              <TextInput.Icon
                icon={showNewPassword ? 'eye-off' : 'eye'}
                onPress={() => setShowNewPassword(!showNewPassword)}
              />
            }
          />

          <TextInput
            label="Confirm New Password"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry={!showConfirmPassword}
            mode="outlined"
            style={styles.input}
            disabled={isLoading}
            left={<TextInput.Icon icon="lock-check" />}
            right={
              <TextInput.Icon
                icon={showConfirmPassword ? 'eye-off' : 'eye'}
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            }
            error={
              confirmPassword.length > 0 && 
              newPassword.length > 0 && 
              newPassword !== confirmPassword
            }
          />

          {confirmPassword.length > 0 && 
           newPassword.length > 0 && 
           newPassword !== confirmPassword && (
            <Text style={styles.errorText}>Passwords do not match</Text>
          )}

          <Text style={styles.helperText}>
            Password must be at least 8 characters long and contain at least one letter and one number.
          </Text>

          {Platform.OS === 'web' && successMessage ? (
            <View style={[styles.messageContainer, styles.successContainer]}>
              <Text style={[styles.messageText, styles.successText]}>{successMessage}</Text>
            </View>
          ) : null}

          {Platform.OS === 'web' && errorMessage ? (
            <View style={[styles.messageContainer, styles.errorContainer]}>
              <Text style={[styles.messageText, styles.errorMessageText]}>{errorMessage}</Text>
            </View>
          ) : null}

          <View style={styles.buttonContainer}>
            <Button
              mode="outlined"
              onPress={() => navigation.goBack()}
              style={[styles.button, styles.cancelButton]}
              disabled={isLoading}
            >
              Cancel
            </Button>

            <Button
              mode="contained"
              onPress={handleChangePassword}
              style={[styles.button, styles.saveButton]}
              loading={isLoading}
              disabled={
                isLoading ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword ||
                newPassword.length < 8
              }
            >
              Change Password
            </Button>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    padding: spacing.lg,
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  title: {
    ...typography.h2,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  subtitle: {
    ...typography.body1,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  input: {
    marginBottom: spacing.md,
  },
  errorText: {
    ...typography.caption,
    color: colors.error,
    marginTop: -spacing.sm,
    marginBottom: spacing.md,
  },
  helperText: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.xl,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  button: {
    flex: 1,
    paddingVertical: spacing.xs,
  },
  cancelButton: {
    marginRight: spacing.sm,
  },
  saveButton: {
    marginLeft: spacing.sm,
  },
  messageContainer: {
    padding: spacing.md,
    borderRadius: 8,
    marginBottom: spacing.md,
    borderWidth: 1,
  },
  messageText: {
    ...typography.body2,
    textAlign: 'center',
  },
  successContainer: {
    backgroundColor: `${colors.success}10`,
    borderColor: colors.success,
  },
  successText: {
    color: colors.success,
  },
  errorContainer: {
    backgroundColor: `${colors.error}10`,
    borderColor: colors.error,
  },
  errorMessageText: {
    color: colors.error,
  },
});

export default ChangePasswordScreen;
